import React, { ComponentProps, useEffect } from 'react';
import { asWidgetProps } from 'Src/types/widget-helpers';
import { classnames } from 'Utils/classnames/classnames.utils';
import Sticky from 'react-stickynode';
import URI from 'urijs';
import { useRouter } from 'next/router';
import { t } from 'Src/i18n';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { withOpenAndScrollToHash } from 'Components/utils/tabs/tabs.hoc';
import { NavigationTabsHeader } from './header/NavigationTabsHeader';
import { getClassNames } from './NavigationTabs.utils';
type NavigationTabsProps = {
  // Component Props
  className?: string;
  isDesktop?: boolean;
  // HOC Props
  id: string;
  initialSelectedItem?: number;
  items: Array<{
    color?: Colors;
    content: Array<React.ReactNode>;
    headerId: string;
    header: {
      title: string;
    };
    onClick?: () => void;
  }>;
};
const _NavigationTabs = ({
  className,
  isDesktop,
  id: mainId = 'NavigationTabs',
  initialSelectedItem = 0,
  items,
}: NavigationTabsProps) => {
  const [initialOffsetLeft, setInitialOffsetLeft] = React.useState(0);
  const refsArray = React.useRef<Array<HTMLDivElement | null>>([]);
  const router = useRouter();
  const getInitialOffsetLeft = (item = 0) => {
    const ref = refsArray.current[item];
    return ref ? ref.offsetLeft : 0;
  };
  useEffect(() => {
    setInitialOffsetLeft(getInitialOffsetLeft(initialSelectedItem));
  }, [initialSelectedItem]);
  if (!items || !items.length) {
    return null;
  }
  const activeItem = items[initialSelectedItem] ?? items[0];
  const content = items.map(({ color, headerId, header: { title }, onClick }, idx) => (
    <div
      className={classnames('border-lightGrey leading-1', getClassNames(items.length), {
        'border-s-1': idx > 0,
      })}
      key={headerId || title}
      ref={(ref) => {
        refsArray.current[idx] = ref;
      }}
    >
      <NavigationTabsHeader
        {...asWidgetProps<ComponentProps<typeof NavigationTabsHeader>>({
          className: 'w-full',
          color: color,
          controlsId: `${headerId}-tab`,
          id: headerId,
          isActive: idx === initialSelectedItem,
          onClick: () => {
            onClick?.();
            const newUrlWithTab = new URI(router.asPath).hash(`${headerId}`).toString();
            router.push(newUrlWithTab);
            refsArray.current[idx]?.focus();
          },
        })}
      >
        {title}
      </NavigationTabsHeader>
    </div>
  ));
  return (
    <div className={classnames('lg:-mt-20', className)} id={mainId}>
      <Sticky
        {...asWidgetProps<ComponentProps<typeof Sticky>>({
          activeClass: 'sticky-bottom--resortNavTabs',
          bottomBoundary: '#NavigationTabsBeacon',
          innerZ: 5,
          top: isDesktop ? 0 : 10,
        })}
      >
        <div className="px-20 lg:pt-20">
          <div className="overflow-hidden rounded-4 border border-lightGrey shadow-diffuse lg:m-auto lg:max-w-site lg:rounded-pill">
            <Scrollable
              {...asWidgetProps<ComponentProps<typeof Scrollable>>({
                backwardLabel: t('common.scrollBackward'),
                forwardLabel: t('common.scrollForward'),
                forceDisplayControls: true,
                initialScrollLeft: initialOffsetLeft,
                theme: 'nav',
              })}
            >
              <div className="flex bg-white" role="tablist">
                {content}
              </div>
            </Scrollable>
          </div>
        </div>
      </Sticky>
      <div
        aria-labelledby={activeItem.headerId}
        className="py-20 outline-none"
        id={`${activeItem.headerId}-tab`}
        role="tabpanel"
      >
        {activeItem.content}
      </div>
      <div id="NavigationTabsBeacon" />
    </div>
  );
};
export const NavigationTabs = withOpenAndScrollToHash(_NavigationTabs);
